/**
 * features/comments/ClassTeacherComment.tsx
 * FEATURE #2 — class teacher writes ONE combined attendance &
 * conduct comment per student, now via the Al-Nur-style dual-pane
 * review workspace (marks + subject comments visible, bottom
 * student-navigator). Scoped to the teacher's own class.
 */
import { useMemo } from 'react';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  isTermEditable,
  canEnterClassCommentFor,
} from '@/lib/permissions';
import { TermKey } from '@/lib/school.config';
import type { TermState } from '@/types';
import ReviewWorkspace from './ReviewWorkspace';

const TEMPLATES = [
  {
    label: 'Attendance',
    phrases: [
      'maintained excellent attendance throughout the term.',
      'attendance has been satisfactory this term.',
      'punctuality and attendance need improvement.',
    ],
  },
  {
    label: 'Conduct',
    phrases: [
      'is respectful, cooperative and works well with peers.',
      'is a positive role model within the class.',
      'should engage more constructively during lessons.',
      'must improve focus and self-discipline in class.',
    ],
  },
  {
    label: 'General',
    phrases: [
      'should continue working hard to improve across subjects.',
      'is encouraged to participate more actively in class.',
      'has shown good improvement and should keep it up.',
    ],
  },
];

export default function ClassTeacherComment({
  year,
  term,
}: {
  year: string;
  term: TermKey;
}) {
  const { classes, activeStudents: students, teachers, termStatuses } = useData();
  const { userData, userRole } = useAuth();

  const termState: TermState =
    termStatuses.find((t) => t.year === year && t.term === term)
      ?.status ?? 'open';
  const editable = isTermEditable(termState, userRole);

  const myClasses = useMemo(
    () =>
      classes.filter((c) =>
        canEnterClassCommentFor(userData, c.id, classes, teachers),
      ),
    [classes, userData, teachers],
  );

  function canEditStudent(studentId: string) {
    const s = students.find((x) => x.id === studentId);
    if (!s) return false;
    return (
      canEnterClassCommentFor(
        userData,
        s.currentClass,
        classes,
        teachers,
      ) && editable
    );
  }

  return (
    <ReviewWorkspace
      year={year}
      term={term}
      field="classTeacherComment"
      title="Class Teacher Review"
      subtitle="One combined attendance & conduct comment per student — review the left panel first"
      classes={myClasses}
      editable={editable}
      termStateLabel={termState}
      canEditStudent={canEditStudent}
      templates={TEMPLATES}
    />
  );
}
